import type {
	RoleAccessState,
	RoleModalData,
	Role,
	EmployeeActiveRole,
	RolesAggregatePayload,
	RoleEmployeesListState,
	GetRoleEmployeesRequest
} from 'src/RoleAccess/types';

import { ROLE_ACCESS_STORE_CONST } from '../actionConstants';

import { failedFor, successFor } from 'reduxStore/sagaUtil';

import { VIEW_ASSIGNED_USERS_INITIAL_PAGE } from '../../RoleAccess/config';

const INITIAL_COUNTS = {
	rolesCount: 0,
	assignedUsers: 0,
	activePermissions: 0
};

const INITIAL_STATE: RoleAccessState = {
	modal: false,
	modalData: null,
	allRoles: [],
	rolesCounts: INITIAL_COUNTS,
	employeeActiveRoles: [],
	roleEmployees: null,
	feature: 0,
	apiStatus: '',
	apiType: ''
};

interface CreateRoleSuccessPayload {
	modal: string;
	modalData: RoleModalData;
}

interface RoleAccessAction {
	type: string;
	payload?: CreateRoleSuccessPayload | RolesAggregatePayload | Role[] | EmployeeActiveRole[] | RoleEmployeesListState | GetRoleEmployeesRequest | RoleModalData | string | boolean | null | Record<string, never>;
}

const roleAccessReducer = (state = INITIAL_STATE, action: RoleAccessAction): RoleAccessState => {
	if (!action) {
		return state;
	}

	const { type, payload } = action;

	switch (type) {
		case successFor(ROLE_ACCESS_STORE_CONST.FETCH_ALL_ROLES): {
			const rolesPayload = payload as Role[];
			return {
				...state,
				allRoles: rolesPayload,
				apiStatus: 'SUCCESS',
				apiType: ROLE_ACCESS_STORE_CONST.FETCH_ALL_ROLES
			};
		}

	case ROLE_ACCESS_STORE_CONST.FETCH_ALL_ROLES: {
		return {
			...state,
			apiStatus: 'LOADING',
			apiType: ROLE_ACCESS_STORE_CONST.FETCH_ALL_ROLES
		};
	}

	case failedFor(ROLE_ACCESS_STORE_CONST.FETCH_ALL_ROLES): {
		return {
			...state,
			apiStatus: 'ERROR',
			apiType: ROLE_ACCESS_STORE_CONST.FETCH_ALL_ROLES
		};
	}

	case ROLE_ACCESS_STORE_CONST.FETCH_ROLES_AGGREGATED: {
		return {
			...state,
			apiStatus: 'LOADING',
			apiType: ROLE_ACCESS_STORE_CONST.FETCH_ROLES_AGGREGATED
		};
	}

	case successFor(ROLE_ACCESS_STORE_CONST.FETCH_ROLES_AGGREGATED): {
		const aggregatePayload = payload as RolesAggregatePayload;
		return {
			...state,
			allRoles: aggregatePayload?.activeRoles || [],
			rolesCounts: aggregatePayload?.counts || INITIAL_COUNTS,
			apiStatus: 'SUCCESS',
			apiType: ROLE_ACCESS_STORE_CONST.FETCH_ROLES_AGGREGATED
		};
	}

	case failedFor(ROLE_ACCESS_STORE_CONST.FETCH_ROLES_AGGREGATED): {
		return {
			...state,
			apiStatus: 'ERROR',
			apiType: ROLE_ACCESS_STORE_CONST.FETCH_ROLES_AGGREGATED
		};
	}

	case ROLE_ACCESS_STORE_CONST.SET_ROLE_MODAL: {
		const modalPayload = payload as string | boolean;
		return {
			...state,
			modal: modalPayload || false
		};
	}

	case ROLE_ACCESS_STORE_CONST.SET_ROLE_MODAL_DATA: {
		const modalDataPayload = payload as RoleModalData | null | undefined;
		return {
			...state,
			modalData: modalDataPayload || null
		};
	}

	case ROLE_ACCESS_STORE_CONST.FETCH_EMPLOYEE_ACTIVE_ROLES: {
		return {
			...state,
			employeeActiveRoles: [],
			apiStatus: 'LOADING',
			apiType: ROLE_ACCESS_STORE_CONST.FETCH_EMPLOYEE_ACTIVE_ROLES
		};
	}

	case successFor(ROLE_ACCESS_STORE_CONST.FETCH_EMPLOYEE_ACTIVE_ROLES): {
		const employeeRolesPayload = payload as EmployeeActiveRole[];
		return {
			...state,
			employeeActiveRoles: employeeRolesPayload || [],
			apiStatus: 'SUCCESS',
			apiType: ROLE_ACCESS_STORE_CONST.FETCH_EMPLOYEE_ACTIVE_ROLES
		};
	}

	case failedFor(ROLE_ACCESS_STORE_CONST.FETCH_EMPLOYEE_ACTIVE_ROLES): {
		return {
			...state,
			employeeActiveRoles: [],
			apiStatus: 'ERROR',
			apiType: ROLE_ACCESS_STORE_CONST.FETCH_EMPLOYEE_ACTIVE_ROLES
		};
	}

	case ROLE_ACCESS_STORE_CONST.FETCH_ROLE_EMPLOYEES: {
		const fetchPayload = payload as GetRoleEmployeesRequest | undefined;
		const requestPage = fetchPayload?.page ?? VIEW_ASSIGNED_USERS_INITIAL_PAGE;
		const isFirstPage = requestPage === VIEW_ASSIGNED_USERS_INITIAL_PAGE;

		if (isFirstPage) {
			return {
				...state,
				roleEmployees: {
					role: '',
					roleDescription: '',
					usersTagged: 0,
					employees: [],
					hasMore: false,
					loading: true,
					error: false
				},
				apiStatus: 'LOADING',
				apiType: ROLE_ACCESS_STORE_CONST.FETCH_ROLE_EMPLOYEES
			};
		}

		return {
			...state,
			roleEmployees: state.roleEmployees
				? { ...state.roleEmployees, loading: true, error: false }
				: null,
			apiStatus: 'LOADING',
			apiType: ROLE_ACCESS_STORE_CONST.FETCH_ROLE_EMPLOYEES
		};
	}

	case successFor(ROLE_ACCESS_STORE_CONST.FETCH_ROLE_EMPLOYEES): {
		const incomingPayload = payload as RoleEmployeesListState;
		const existingEmployees = state.roleEmployees?.employees || [];
		const isFirstPage = existingEmployees.length === 0;

		return {
			...state,
			roleEmployees: {
				...incomingPayload,
				employees: isFirstPage
					? incomingPayload.employees
					: [...existingEmployees, ...incomingPayload.employees]
			},
			apiStatus: 'SUCCESS',
			apiType: ROLE_ACCESS_STORE_CONST.FETCH_ROLE_EMPLOYEES
		};
	}

	case failedFor(ROLE_ACCESS_STORE_CONST.FETCH_ROLE_EMPLOYEES): {
		const hasExistingData = (state.roleEmployees?.employees?.length ?? 0) > 0;

		return {
			...state,
			roleEmployees: hasExistingData && state.roleEmployees
				? { ...state.roleEmployees, loading: false, error: true }
				: {
					role: state.roleEmployees?.role ?? '',
					roleDescription: state.roleEmployees?.roleDescription ?? '',
					usersTagged: state.roleEmployees?.usersTagged ?? 0,
					employees: [],
					hasMore: false,
					loading: false,
					error: true
				},
			apiStatus: 'ERROR',
			apiType: ROLE_ACCESS_STORE_CONST.FETCH_ROLE_EMPLOYEES
		};
	}

	case ROLE_ACCESS_STORE_CONST.ROLE_ACCESS_FEATURE_DATA: {
		const featurePayload = payload as { feature: number };
		return {
			...state,
			feature: featurePayload?.feature ?? 0
		};
	}

	default:
		return state;
	}
};

export default roleAccessReducer;
